import {
  hasRole,
  type ActiveTenantDto,
  type AgentDto,
  type EndpointDto,
  type MetricsDto,
  type ProviderDto,
  type RunDto,
} from '@agent-platform/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowUpRight,
  Bot,
  Check,
  FlaskConical,
  Plus,
  ScrollText,
  Users,
  Waypoints,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { type ReactNode, useMemo, useRef } from 'react';
import { AgentBot } from '@/components/agent-bot';
import { PageHeader } from '@/components/page-header';
import { CountUp } from '@/components/reactbits/CountUp';
import Ferrofluid from '@/components/reactbits/Ferrofluid';
import { SpotlightCard } from '@/components/reactbits/SpotlightCard';
import { RequireTenant } from '@/components/require-tenant';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import {
  formatCompact,
  formatDuration,
  formatNumber,
  formatPercent,
  formatRelative,
  ROLE_LABELS,
  RUN_SOURCE_LABELS,
} from '@/lib/format';
import { useMe, usePaged } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { RunStatusBadge } from './traces';

const DAY_MS = 86_400_000;
/** Runs still "running" after this long have lost their worker; they don't count as live. */
const STALLED_MS = 15 * 60_000;

function greeting() {
  const h = new Date().getHours();
  return h < 5
    ? 'Working late'
    : h < 12
      ? 'Good morning'
      : h < 18
        ? 'Good afternoon'
        : 'Good evening';
}

/** Staggered entrance for the page's sections. */
const enter = (i: number) => ({
  className: 'animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-500',
  style: { animationDelay: `${i * 70}ms` },
});

function Sparkline({ values, tone = 'default' }: { values: number[]; tone?: 'default' | 'bad' }) {
  if (values.length < 2 || values.every((v) => v === 0))
    return <div className="bg-border/60 h-px w-full self-end" aria-hidden />;
  const max = Math.max(...values, 1);
  const step = 100 / (values.length - 1);
  const pts = values.map((v, i) => `${(i * step).toFixed(2)},${(30 - (v / max) * 28).toFixed(2)}`);
  const line = `M ${pts.join(' L ')}`;
  return (
    <svg viewBox="0 0 100 32" preserveAspectRatio="none" className="h-10 w-full" aria-hidden>
      <path
        d={`${line} L 100,32 L 0,32 Z`}
        className={tone === 'bad' ? 'fill-destructive/10' : 'fill-tint-blue/10'}
      />
      <path
        d={line}
        fill="none"
        strokeWidth="1.75"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        className={tone === 'bad' ? 'stroke-destructive' : 'stroke-tint-blue'}
      />
    </svg>
  );
}

function Kpi({
  label,
  value,
  format,
  hint,
  spark,
  tone,
  index,
}: {
  label: string;
  value: number | null;
  format: (n: number) => string;
  hint: ReactNode;
  spark: number[];
  tone?: 'default' | 'bad';
  index: number;
}) {
  const e = enter(index + 1);
  return (
    <Link
      to="/metrics"
      className={cn(
        'group focus-visible:ring-ring/50 rounded-2xl outline-none focus-visible:ring-3',
        e.className,
      )}
      style={e.style}
    >
      <SpotlightCard className="grid h-full gap-2 p-4 transition-[translate,box-shadow] duration-300 group-hover:-translate-y-0.5 group-hover:shadow-lg">
        <div className="text-muted-foreground flex items-center justify-between text-xs">
          {label}
          <ArrowUpRight
            className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden
          />
        </div>
        <div className="text-3xl font-semibold tracking-tight tabular-nums">
          {value === null ? '—' : <CountUp to={value} format={format} />}
        </div>
        <div className="text-muted-foreground text-xs">{hint}</div>
        <Sparkline values={spark} tone={tone} />
      </SpotlightCard>
    </Link>
  );
}

function Panel({
  title,
  aside,
  children,
  className,
  style,
}: {
  title: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <section className={cn('bg-surface rounded-2xl border', className)} style={style}>
      <header className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <h2 className="text-sm font-medium">{title}</h2>
        {aside}
      </header>
      {children}
    </section>
  );
}

function LiveActivity({ runs, agentName }: { runs: RunDto[]; agentName: (id: string) => string }) {
  // Rows that arrive after the first load slide in; the first page renders at once.
  const seen = useRef<Set<string> | null>(null);
  const fresh = new Set<string>();
  if (seen.current) for (const r of runs) if (!seen.current.has(r.id)) fresh.add(r.id);
  seen.current = new Set(runs.map((r) => r.id));

  if (runs.length === 0)
    return (
      <div className="grid place-items-center gap-2 px-6 py-14 text-center">
        <FlaskConical className="text-muted-foreground size-5" aria-hidden />
        <p className="text-sm font-medium">No runs yet</p>
        <p className="text-muted-foreground max-w-xs text-xs">
          Send your first message from the playground; every run shows up here as it happens.
        </p>
        <Button size="sm" variant="outline" asChild className="mt-1">
          <Link to="/playground">Open playground</Link>
        </Button>
      </div>
    );
  return (
    <ol className="divide-y" aria-label="Recent runs">
      {runs.map((r) => (
        <li
          key={r.id}
          className={cn(fresh.has(r.id) && 'animate-in fade-in slide-in-from-top-2 duration-500')}
        >
          <Link
            to="/traces/runs/$runId"
            params={{ runId: r.id }}
            className="hover:bg-accent/60 focus-visible:bg-accent/60 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-2.5 transition-colors outline-none"
          >
            <RunStatusBadge status={r.status} />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{agentName(r.agentId)}</span>
              <span className="text-muted-foreground block truncate text-xs">
                {RUN_SOURCE_LABELS[r.source]} · <span className="font-mono">{r.model}</span>
              </span>
            </span>
            <span className="text-muted-foreground grid justify-items-end text-xs tabular-nums">
              <span>{formatRelative(r.startedAt)}</span>
              <span>
                {formatDuration(r.latencyMs)}
                {r.inputTokens !== null &&
                  ` · ${formatCompact((r.inputTokens ?? 0) + (r.outputTokens ?? 0))} tok`}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </ol>
  );
}

function TopAgents({ agents, metrics }: { agents: AgentDto[]; metrics: MetricsDto | undefined }) {
  const runsOf = new Map(metrics?.byAgent.map((a) => [a.agentId, a.runs]) ?? []);
  const sorted = [...agents]
    .sort(
      (a, b) =>
        (runsOf.get(b.id) ?? 0) - (runsOf.get(a.id) ?? 0) || b.updatedAt.localeCompare(a.updatedAt),
    )
    .slice(0, 5);
  const max = Math.max(1, ...sorted.map((a) => runsOf.get(a.id) ?? 0));
  if (sorted.length === 0)
    return (
      <p className="text-muted-foreground px-4 py-8 text-center text-sm">
        No agents yet.{' '}
        <Link to="/agents" className="text-foreground underline underline-offset-4">
          Create one
        </Link>
        .
      </p>
    );
  return (
    <ul className="grid gap-1 p-2" aria-label="Agents by runs today">
      {sorted.map((a) => {
        const runs = runsOf.get(a.id) ?? 0;
        return (
          <li key={a.id}>
            <Link
              to="/agents/$agentId"
              params={{ agentId: a.id }}
              className="hover:bg-accent/60 focus-visible:bg-accent/60 grid gap-1.5 rounded-lg px-2 py-2 transition-colors outline-none"
            >
              <span className="flex items-center gap-2 text-sm">
                <span
                  className={cn(
                    'size-1.5 shrink-0 rounded-full',
                    a.status === 'active' ? 'bg-tint-green' : 'bg-muted-foreground/40',
                  )}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate font-medium">{a.name}</span>
                <span className="text-muted-foreground text-xs tabular-nums">
                  {a.publishedVersion ? `v${a.publishedVersion}` : 'Draft'} · {formatNumber(runs)}{' '}
                  {runs === 1 ? 'run' : 'runs'}
                </span>
              </span>
              <span className="bg-muted h-1 overflow-hidden rounded-full" aria-hidden>
                <span
                  className="bg-tint-blue/70 block h-full rounded-full transition-[width] duration-700"
                  style={{ width: `${(runs / max) * 100}%` }}
                />
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function GettingStarted({ steps }: { steps: Array<{ label: string; done: boolean; to: string }> }) {
  const done = steps.filter((s) => s.done).length;
  return (
    <div className="grid gap-3 p-4">
      <div className="bg-muted h-1.5 overflow-hidden rounded-full" aria-hidden>
        <div
          className="bg-tint-green h-full rounded-full transition-[width] duration-700"
          style={{ width: `${(done / steps.length) * 100}%` }}
        />
      </div>
      <ol className="grid gap-1" aria-label="Setup steps">
        {steps.map((s, i) => (
          <li key={s.label}>
            <Link
              to={s.to as '/'}
              className="hover:bg-accent/60 flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors"
            >
              <span
                className={cn(
                  'grid size-5 shrink-0 place-items-center rounded-full border text-[11px] tabular-nums',
                  s.done && 'bg-tint-green border-transparent text-white',
                )}
              >
                {s.done ? <Check className="size-3" aria-label="Done" /> : i + 1}
              </span>
              <span className={cn(s.done && 'text-muted-foreground line-through')}>{s.label}</span>
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}

function OverviewContent({ tenant, firstName }: { tenant: ActiveTenantDto; firstName: string }) {
  const { resolvedTheme } = useTheme();
  const admin = hasRole(tenant.role, 'admin');
  const metrics = useQuery({
    queryKey: ['overview-metrics', tenant.id],
    queryFn: () => api<MetricsDto>(`/metrics?from=${new Date(Date.now() - DAY_MS).toISOString()}`),
    refetchInterval: 30_000,
  });
  const runs = useQuery({
    queryKey: ['overview-runs', tenant.id],
    queryFn: () => api<{ items: RunDto[] }>('/runs?limit=8'),
    refetchInterval: 10_000,
  });
  const agents = usePaged<AgentDto>(['agents', tenant.id], '/agents');
  const providers = usePaged<ProviderDto>(['providers', tenant.id], '/providers', admin);
  const endpoints = usePaged<EndpointDto>(['endpoints', tenant.id], '/endpoints', admin);

  const agentList = agents.data?.pages.flatMap((p) => p.items) ?? [];
  const recent = runs.data?.items ?? [];
  const live = recent.filter(
    (r) => r.status === 'running' && Date.now() - new Date(r.startedAt).getTime() < STALLED_MS,
  ).length;
  const m = metrics.data;
  const name = (id: string) => agentList.find((a) => a.id === id)?.name ?? 'Deleted agent';

  const messages = useMemo(() => {
    const lines: string[] = [];
    if (live) lines.push(`I'm watching ${live} run${live === 1 ? '' : 's'} in progress right now.`);
    if (!m || m.totals.runs === 0) {
      lines.push(`Hi ${firstName || 'there'}! It's quiet today. Try an agent in the playground?`);
    } else {
      lines.push(
        `Hi ${firstName || 'there'}! ${formatNumber(m.totals.runs)} runs in the last 24 hours.`,
      );
      lines.push(
        m.totals.failed
          ? `${formatNumber(m.totals.failed)} failed (${formatPercent(m.totals.errorRate)}). Traces show why.`
          : 'No failed runs today. Nice.',
      );
      if (m.totals.latencyMs.p95 !== null)
        lines.push(`p95 latency is ${formatDuration(m.totals.latencyMs.p95)}.`);
      lines.push(
        `${formatCompact(m.totals.inputTokens + m.totals.outputTokens)} tokens used today.`,
      );
      const top = [...m.byAgent].sort((a, b) => b.runs - a.runs)[0];
      if (top) lines.push(`${top.name} is the busiest agent today.`);
    }
    return lines;
  }, [m, live, firstName]);

  const steps = [
    {
      label: 'Connect a model provider',
      done: (providers.data?.pages[0]?.items.length ?? 0) > 0,
      to: '/llm-setup',
    },
    { label: 'Create an agent', done: agentList.length > 0, to: '/agents' },
    {
      label: 'Publish a version',
      done: agentList.some((a) => a.publishedVersionId),
      to: '/agents',
    },
    {
      label: 'Expose it as an endpoint',
      done: (endpoints.data?.pages[0]?.items.length ?? 0) > 0,
      to: '/endpoints',
    },
    { label: 'Send it a message', done: recent.length > 0, to: '/playground' },
  ];
  const setupLoaded = !providers.isPending && !endpoints.isPending && !agents.isPending;
  const showSetup = admin && setupLoaded && steps.some((s) => !s.done);
  const series = m?.series ?? [];
  const dark = resolvedTheme !== 'light';
  const hero = enter(0);

  return (
    <div className="grid gap-4">
      <section
        className={cn(
          'bg-surface relative isolate overflow-hidden rounded-3xl border',
          hero.className,
        )}
        style={hero.style}
      >
        {/* React Bits Ferrofluid: reacts to the pointer and pulses on click. */}
        <div
          className="absolute inset-0 -z-10 [mask-image:linear-gradient(to_right,transparent_10%,black_60%)]"
          aria-hidden
        >
          <Ferrofluid
            key={dark ? 'dark' : 'light'}
            colors={dark ? ['#ffffff', '#ffffff', '#ffffff'] : ['#2b2b33', '#5b5b66', '#9a9aa6']}
            lightMode={!dark}
            spikeHeight={0.8}
            sharpness={2.5}
            tilt={46}
            opacity={dark ? 0.75 : 0.6}
            mouseInteraction
            clickPulse
          />
        </div>
        <div className="grid items-center gap-6 p-6 md:p-8 lg:grid-cols-[minmax(0,1fr)_auto]">
          <div className="grid max-w-xl content-center gap-4">
            <span className="text-muted-foreground inline-flex items-center gap-2 text-xs font-medium">
              <span className="relative flex size-2" aria-hidden>
                {live > 0 && (
                  <span className="bg-tint-blue absolute inline-flex size-full animate-ping rounded-full opacity-60" />
                )}
                <span
                  className={cn(
                    'relative inline-flex size-2 rounded-full',
                    live > 0 ? 'bg-tint-blue' : 'bg-tint-green',
                  )}
                />
              </span>
              {live > 0
                ? `${live} run${live === 1 ? '' : 's'} in progress`
                : 'All quiet · nothing running'}
            </span>
            <h2 className="text-3xl leading-tight font-semibold tracking-tight text-balance md:text-4xl">
              {greeting()}
              {firstName ? `, ${firstName}` : ''}.
            </h2>
            <p className="text-muted-foreground max-w-md text-sm text-pretty">
              You're in <strong className="text-foreground font-medium">{tenant.name}</strong> as{' '}
              {ROLE_LABELS[tenant.role].toLowerCase()}. Here's what your agents have been doing in
              the last 24 hours.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              {admin && (
                <Button asChild>
                  <Link to="/agents">
                    <Plus aria-hidden /> New agent
                  </Link>
                </Button>
              )}
              {admin && (
                <Button asChild variant="outline" className="bg-surface/70 backdrop-blur">
                  <Link to="/playground">
                    <FlaskConical aria-hidden /> Playground
                  </Link>
                </Button>
              )}
              <Button asChild variant="outline" className="bg-surface/70 backdrop-blur">
                <Link to="/traces">
                  <Waypoints aria-hidden /> Traces
                </Link>
              </Button>
            </div>
          </div>
          <AgentBot busy={live > 0} messages={messages} className="justify-self-center" />
        </div>
      </section>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.isPending ? (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)
        ) : (
          <>
            <Kpi
              index={0}
              label="Runs · 24h"
              value={m?.totals.runs ?? null}
              format={(n) => formatNumber(Math.round(n))}
              hint={`${formatNumber(m?.totals.succeeded ?? 0)} succeeded · ${formatNumber(m?.totals.workflowRuns ?? 0)} workflow runs`}
              spark={series.map((s) => s.runs)}
            />
            <Kpi
              index={1}
              label="Error rate"
              value={m?.totals.errorRate ?? null}
              format={(n) => formatPercent(n)}
              hint={`${formatNumber(m?.totals.failed ?? 0)} failed runs`}
              spark={series.map((s) => s.failed)}
              tone="bad"
            />
            <Kpi
              index={2}
              label="p95 latency"
              value={m?.totals.latencyMs.p95 ?? null}
              format={(n) => formatDuration(n)}
              hint={`p50 ${formatDuration(m?.totals.latencyMs.p50)}`}
              spark={series.map((s) => s.p95LatencyMs ?? 0)}
            />
            <Kpi
              index={3}
              label="Tokens · 24h"
              value={m ? m.totals.inputTokens + m.totals.outputTokens : null}
              format={(n) => formatCompact(Math.round(n))}
              hint={`${formatCompact(m?.totals.inputTokens ?? 0)} in · ${formatCompact(m?.totals.outputTokens ?? 0)} out`}
              spark={series.map((s) => s.tokens)}
            />
          </>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          title="Live activity"
          className={cn('lg:col-span-2', enter(5).className)}
          style={enter(5).style}
          aside={
            <span className="text-muted-foreground inline-flex items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <span className="bg-tint-green size-1.5 animate-pulse rounded-full" aria-hidden />
                Updates every 10 s
              </span>
              <Link
                to="/traces"
                className="hover:text-foreground underline-offset-4 hover:underline"
              >
                All traces
              </Link>
            </span>
          }
        >
          {runs.isPending ? (
            <div className="grid gap-2 p-4">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : (
            <LiveActivity runs={recent} agentName={name} />
          )}
        </Panel>

        <div className="grid content-start gap-4">
          {showSetup && (
            <Panel
              title="Get set up"
              aside={
                <span className="text-muted-foreground text-xs tabular-nums">
                  {steps.filter((s) => s.done).length}/{steps.length}
                </span>
              }
              className={enter(6).className}
              style={enter(6).style}
            >
              <GettingStarted steps={steps} />
            </Panel>
          )}
          <Panel
            title={
              <span className="inline-flex items-center gap-2">
                <Bot className="text-muted-foreground size-4" aria-hidden /> Agents
              </span>
            }
            aside={
              <Link
                to="/agents"
                className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline"
              >
                All agents
              </Link>
            }
            className={enter(7).className}
            style={enter(7).style}
          >
            {agents.isPending ? (
              <div className="grid gap-2 p-4">
                <Skeleton className="h-8" />
                <Skeleton className="h-8" />
              </div>
            ) : (
              <TopAgents agents={agentList} metrics={m} />
            )}
          </Panel>
          <Panel
            title="Workspace"
            className={enter(8).className}
            style={enter(8).style}
            aside={
              <span
                className={cn(
                  'rounded-md px-1.5 py-0.5 text-xs font-medium',
                  tenant.status === 'active'
                    ? 'bg-tint-green/12 text-tint-green'
                    : 'bg-destructive/10 text-destructive',
                )}
              >
                {tenant.status === 'active' ? 'Active' : 'Suspended'}
              </span>
            }
          >
            <dl className="grid grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-3 text-sm">
              <dt className="text-muted-foreground">Tenant</dt>
              <dd className="truncate">{tenant.name}</dd>
              <dt className="text-muted-foreground">Slug</dt>
              <dd className="truncate font-mono text-xs leading-5">{tenant.slug}</dd>
              <dt className="text-muted-foreground">Your role</dt>
              <dd>{ROLE_LABELS[tenant.role]}</dd>
            </dl>
            <div className="flex flex-wrap gap-2 border-t px-4 py-3">
              <Button asChild size="sm" variant="outline">
                <Link to="/members">
                  <Users aria-hidden /> Members
                </Link>
              </Button>
              {admin && (
                <Button asChild size="sm" variant="ghost">
                  <Link to="/audit-log">
                    <ScrollText aria-hidden /> Audit log
                  </Link>
                </Button>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export function OverviewPage() {
  const me = useMe();
  const firstName = me.user.name.trim().split(/\s+/)[0] ?? '';
  return (
    <>
      <PageHeader title={`Welcome${me.user.name ? `, ${me.user.name}` : ''}`} />
      <RequireTenant>
        {(tenant) => <OverviewContent tenant={tenant} firstName={firstName} />}
      </RequireTenant>
    </>
  );
}
